import CustomStore from 'devextreme/data/custom_store';
import { isEmpty, setWith } from 'lodash';
import axios from '@/axios-instance';
import filterToWhere from '@/lib/dx/filter/filter-to-where';
import { resolve } from '@/lib/util';

function prepareValues(values) {
  const newValues = {};

  Object.entries(values)
    .forEach(([key, value]) => setWith(newValues, key, value));

  return newValues;
}

export default function createBackendStore(resourcePath, {
  keyProperty = 'id',
  fields,
  relations,
  toRow,
  valuesFilter,
  afterInsertOrUpdate,
  onLoaded,
} = {}) {
  return new CustomStore({
    key: keyProperty,
    async load(options) {
      const fieldsValue = resolve(fields);
      const relationsValue = resolve(relations);

      const filter = {
        skip: options.skip,
        limit: options.take,
      };

      if (options.sort) {
        filter.order = options.sort
          .map(({ selector, desc }) => `${selector} ${desc ? 'DESC' : 'ASC'}`);
      }

      if (options.filter) {
        filter.where = filterToWhere(options.filter);
      } else if (options.searchValue) {
        const { searchExpr, searchOperation, searchValue } = options;
        filter.where = filterToWhere([searchExpr, searchOperation, searchValue]);
      }

      if (fieldsValue) {
        filter.fields = {};
        fieldsValue.forEach((field) => {
          filter.fields[field] = true;
        });
      }

      if (relationsValue) {
        filter.include = relationsValue.map(relation => ({ relation }));
      }

      const [{ data: { count: totalCount } }, { data: items }] = await Promise.all([
        axios.get(`${resourcePath}/count`, { params: { where: filter.where } }),
        axios.get(resourcePath, { params: { filter } }),
      ]);

      const data = toRow ? items.map(item => toRow(item)) : items;

      if (onLoaded) {
        setTimeout(onLoaded, 0);
      }

      return { totalCount, data };
    },
    async byKey(key) {
      const { data } = await axios.get(`${resourcePath}/${key}`);
      return data;
    },
    async insert(values) {
      const preparedValues = prepareValues(values);
      const filteredValues = valuesFilter ? valuesFilter(preparedValues) : values;

      const { data: { id: key } } = await axios.post(resourcePath, filteredValues);

      if (afterInsertOrUpdate) {
        await afterInsertOrUpdate(key, preparedValues, 'insert');
      }
    },
    async update(key, values) {
      const preparedValues = prepareValues(values);
      const filteredValues = valuesFilter ? valuesFilter(preparedValues) : values;

      if (!isEmpty(filteredValues)) {
        await axios.patch(`${resourcePath}/${key}`, filteredValues);
      }

      if (afterInsertOrUpdate) {
        await afterInsertOrUpdate(key, preparedValues, 'update');
      }
    },
    async remove(key) {
      await axios.delete(`${resourcePath}/${key}`);
    },
  });
}
